/**
 * Sort by dropdown (snippets/sort-dropdown.liquid).
 *
 * Shows the native sort select as a styled list. Picking an option sets the
 * select's value and fires the same bubbling `input` event the select would,
 * so facets.js sorts the results exactly as before.
 */
if (!customElements.get('sort-dropdown')) {
  customElements.define(
    'sort-dropdown',
    class SortDropdown extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.button = this.querySelector('.sort-dropdown__button');
        this.list = this.querySelector('.sort-dropdown__list');

        // The button replaces the select for keyboard and screen reader users.
        this.select.tabIndex = -1;
        this.select.setAttribute('aria-hidden', 'true');

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.toggle(false);
        };
        document.addEventListener('click', this.onDocumentClick);

        this.button.addEventListener('click', () => this.toggle());
        this.list.addEventListener('click', (event) => {
          const option = event.target.closest('[role="option"]');
          if (option) this.choose(option);
        });
        this.addEventListener('keydown', this.onKeydown.bind(this));
        // Tabbing away closes the list.
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.toggle(false);
        });
        // The "Sort by" label points at the hidden select; hand focus to the button.
        this.select.addEventListener('focus', () => this.button.focus());
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      get options() {
        return Array.from(this.list.querySelectorAll('[role="option"]'));
      }

      toggle(open = this.list.hidden) {
        this.list.hidden = !open;
        this.button.setAttribute('aria-expanded', open);
        if (open) (this.list.querySelector('[aria-selected="true"]') || this.options[0]).focus();
      }

      choose(option) {
        this.options.forEach((item) => item.setAttribute('aria-selected', item === option));
        this.querySelector('.sort-dropdown__current').textContent = option.textContent.trim();
        this.toggle(false);
        this.button.focus();

        if (this.select.value === option.dataset.value) return;
        this.select.value = option.dataset.value;
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
      }

      onKeydown(event) {
        if (event.key === 'Escape' && !this.list.hidden) {
          event.preventDefault();
          this.toggle(false);
          this.button.focus();
          return;
        }

        const option = event.target.closest('[role="option"]');
        if (!option) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.toggle(true);
          }
          return;
        }

        const options = this.options;
        const index = options.indexOf(option);
        const targets = {
          ArrowDown: options[Math.min(index + 1, options.length - 1)],
          ArrowUp: options[Math.max(index - 1, 0)],
          Home: options[0],
          End: options[options.length - 1],
        };

        if (targets[event.key]) {
          event.preventDefault();
          targets[event.key].focus();
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.choose(option);
        }
      }
    }
  );
}
